import { CART_MODULE } from "@/api_framework/api_config";
import { ICartSearchItemClickResponse, ICartSearchItemClickResponseData } from "@/api_framework/api_modals/Cart";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { debounce, has } from "lodash";
import { useCallback, useState } from "react";
import { useAPIUtils } from "../../useAPIUtils";
import toast from "react-hot-toast";

AxiosCurlirize(axios, () => { });

export interface ICartItemDetailsPayload {
  id: string;
  pet_id?: string;
  clinic_id?: string;
  selected_item: string;
  cart_id?: string;
  availability?: string;
  apply_membership_coupons?: boolean;
  edit_lot_details?: boolean;
  is_hd_stock?: boolean;
  customer_id: string;
  unit_price: string;
  pincode: string;
  source?: number;
  is_deworming_item?: boolean;
  gift_id?: string;
  reference_id?: string;
  coupon_id?: string;
}

export const useCartSearchItemClickAPI = () => {
  const configuration = useConfiguration();
  const [alreadyAddedURIDError, setAlreadyAddedURIDError] = useState<string | null>(null);
  const [data, setData] = useState<ICartSearchItemClickResponseData[]>([]);
  const [isPetModelOpen, setIsPetModelOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const [errorSource, setErrorSource] = useState<number | null>(null);
  const [pendingPayload, setPendingPayload] = useState<ICartItemDetailsPayload | null>(null);



  const debounceRemoveAlreadyAddedURIDError = debounce(() => {
    setAlreadyAddedURIDError(null);
  }, 1500);


  const execute = useCallback(
    async (payload: ICartItemDetailsPayload) => {
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const response: ICartSearchItemClickResponseData[] | null = await axios
          .get(`${CART_MODULE?.CART_SEARCH_ITEM_CLICK?.baseURL}`, {
            params: {
              ...payload,
            },
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<ICartSearchItemClickResponse>) => {
            if (res.data.status === true) {
              setData(res.data.data);
              return res.data.data;
            } else {
              setData([]);
              return null;
            }
          })
          .catch((e: AxiosError) => {
            if (e.code === "ERR_NETWORK") {
              toast.error("Server Error 500");
            }
            sendCurl(e.config["curlCommand"]);
            captureError(e);
            if (e.response.status === 400) {
              const responseData = e.response.data as any;
              if (has(responseData.data, "unique_row_id")) {
                setAlreadyAddedURIDError(responseData.data.unique_row_id);
                debounceRemoveAlreadyAddedURIDError();
              }
              const source = responseData.data?.source;

              if (source && /\d/.test(String(source))) {
                setErrorSource(Number(source));
                setPendingPayload(payload);
                setIsPetModelOpen(true);
              }

            }
            return null;
          });
        return response;
      } catch (e) {
        captureError(e);
      }
    },
    [dispatch],
  );

  const retry = useCallback(async () => {
    if (!pendingPayload) return null;

    setIsPetModelOpen(false);

    const updatedPayload = {
      ...pendingPayload,
      source: errorSource ?? undefined,
    };

    const result = await execute(updatedPayload);
    

    if (result) {
      setPendingPayload(null);
      setErrorSource(null);
    }

    return {
      result,
      payload: updatedPayload,
    };
  }, [pendingPayload, errorSource, execute]);



  return { data, execute, alreadyAddedURIDError, isPetModelOpen, retry, setIsPetModelOpen, errorSource };
};
